import { AfterViewInit, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewContainerRef, ViewEncapsulation } from "@angular/core";
import { Person } from "@common/ADAPT.Common.Model/person/person";
import { AdaptClientConfiguration } from "@common/configuration/adapt-client-configuration";
import { IdentityStorageService } from "@common/identity/identity-storage.service";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { IChatEditorAction } from "@common/lib/chat-ai/ai-editor-actions.service";
import { CommonDataService } from "@common/lib/data/common-data.service";
import { QueuedCaller } from "@common/lib/queued-caller/queued-caller";
import { ElementUtilities } from "@common/lib/utilities/element-utilities";
import { StringUtilities } from "@common/lib/utilities/string-utilities";
import { StorageService } from "@common/storage/storage.service";
import { PeopleQueryUtilities } from "@common/user/people-query-utilities";
import { BaseComponent } from "@common/ux/base.component/base.component";
import dxScrollView from "devextreme/ui/scroll_view";
import FroalaEditor, { FroalaOptions, ListType, ToolbarButtons } from "froala-editor";
import { debounceTime, Subject, throttleTime } from "rxjs";
import Tribute, { TributeItem } from "tributejs";
import { IFocusable } from "../../adapt-common-dialog/focusable";
import { HtmlEditorService } from "../html-editor.service";
import { MentionPersonComponent } from "../mention-person/mention-person.component";
import { ParagraphFormatFroalaAction } from "./paragraph-format-froala-action";

export const MentionBoxClass = "adapt-mention-box";

interface ITributeItemType {
    key: string;
    value: number;
    person: Person;
}

@Component({
    selector: "adapt-html-editor-froala",
    template: `
        <div class="adaptEditor">
          <div [froalaEditor]="froalaOptions"
          [froalaModel]="froalaContents"></div>
        </div>
        @if (isInCodeView) {
          <div
            class="alert alert-info mt-3">
            <p>Please switch out of code view to save changes.</p>
          </div>
        }
        `,
    styleUrls: ["./html-editor-froala.component.scss"],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class AdaptHtmlEditorFroalaComponent extends BaseComponent implements OnInit, OnChanges, IFocusable, AfterViewInit, OnDestroy {
    private static hasBeenInitialised = false;

    @Input() public contents?: string | null;
    @Output() public contentsChange = new EventEmitter<string>();
    @Output() public isValidChange = new EventEmitter<boolean>();
    @Output() public contentHeightChanged = new EventEmitter<number>();

    @Input() public minHeight?: number;
    @Input() public forceSize?: "standard" | "compact";
    @Input() public placeholderText = "Type something";
    @Input() public required?: boolean;
    @Input() public onlyShowToolbarWhenFocussed?: boolean;
    @Input() public readonly = false;
    @Input() public showCodeView = true;
    @Input() public useMentions = false;

    @Input() public editorActions?: IChatEditorAction[];

    public editorQueuedCaller = new QueuedCaller<FroalaEditor>();
    public froalaOptions?: Partial<FroalaOptions>;
    public froalaContents?: string;
    public isInCodeView = false;

    private readonly uploadUrl: string;
    private _token?: string;
    private currentContents?: string;
    private hasFocus = false;
    private maskInternalContentsUpdate = false;
    private froalaContentHasBeenInitialised = false;
    private contentScroller?: dxScrollView;
    private contentScrollerUpdater$ = new Subject<void>();
    private frElementResizeObserver?: ResizeObserver;

    public constructor(
        storageService: StorageService,
        private identityStorage: IdentityStorageService,
        htmlEditorService: HtmlEditorService,
        elementRef: ElementRef,
        private commonDataService: CommonDataService,
        private viewContainerRef: ViewContainerRef,
    ) {
        super(elementRef);
        if (!AdaptHtmlEditorFroalaComponent.hasBeenInitialised) {
            htmlEditorService.registerEditorActionCallback(() => ParagraphFormatFroalaAction.registerAction());
            htmlEditorService.initialiseRegisteredActionCallbacks();
            AdaptHtmlEditorFroalaComponent.hasBeenInitialised = true;
        }

        this.uploadUrl = storageService.storeUri();
        this.contentScrollerUpdater$.pipe(
            debounceTime(500),
            this.takeUntilDestroyed(),
        ).subscribe(() => this.updateContentScroller());
    }

    public ngOnInit() {
        this._token = this.identityStorage.accessToken;
        this.froalaOptions = this.generateFroalaOptions();
    }

    public ngOnDestroy() {
        super.ngOnDestroy();
        this.frElementResizeObserver?.disconnect();
    }

    @HostListener("focusin")
    public onFocusIn() {
        // With a dxScrollView around frElement, there are layers of div's and wrappers between this component's native element and the actual editor element
        // - I tried setting tabIndex -1 for the wrapper that I created to inject dxScrollView but that didn't solve the focus issue.
        //   dxScrollView has more layers of elements in between. Would be a nuisance to set tabIndex on all of them.
        // - Easier if I call this existing focus function that Greg has written.
        this.getElementToFocus(false);
    }

    public ngAfterViewInit() {
        const element = this.elementRef!.nativeElement as HTMLElement;
        ElementUtilities.waitUntilDescendantVisible(element, ".fr-element").pipe(
            this.takeUntilDestroyed(),
        ).subscribe((frElement) => {
            // Create a wrapper div for the scroll view
            // - .fr-element is the content of the editor
            // - parent of .fr-element is .fr-wrapper and that's used by froala to be at the same level as top and bottom toolbars - so don't touch it
            //   - fr-wrapper is already set to 100% height from the scss - can either overflow auto there or wrap a dxScrollView around the .fr-element
            //   - go for dxScrollView here as it looks better
            const scrollWrapper = document.createElement("div");
            // Insert the wrapper before the frElement
            frElement.parentNode?.insertBefore(scrollWrapper, frElement);
            // Move frElement into the wrapper (this removes it from its current parent)
            scrollWrapper.appendChild(frElement);

            // Initialize dxScrollView on the wrapper
            this.contentScroller = new dxScrollView(scrollWrapper, {
                showScrollbar: "always",
            });

            this.frElementResizeObserver?.disconnect();
            // observe any size changes to emit to parent to control height
            // - I want max-height on html-editor but scrollbar not working property without setting height :(
            this.frElementResizeObserver = ElementUtilities.onElementSizeChanged(frElement, () => this.contentHeightChanged.emit(frElement.getBoundingClientRect().height));
        });
    }

    public refreshContentScroller() {
        this.contentScroller?.update();
    }

    public ngOnChanges(changes: SimpleChanges) {
        if (changes.contents) {
            this.updateFroalaContent(changes.contents.currentValue);
        }

        if (changes.readonly) {
            this.setIsDisabled(this.readonly);
        }

        // wait until options has been set already, else we get error that token is not set.
        if (this.froalaOptions && (changes.placeholderText || changes.forceSize || changes.minHeight)) {
            this.froalaOptions = this.generateFroalaOptions();
        }
    }

    public getElementToFocus(moveCursorToEnd = true) {
        // wait for editor to be ready
        setTimeout(() => {
            const element = this.elementRef!.nativeElement.querySelector(".fr-element");

            // focus element
            element?.focus();
            if (moveCursorToEnd) {
                this.moveCursorToEnd();
            }
        });

        // don't return anything - we have handled the focus
        return undefined;
    }

    private get token() {
        return this._token;
    }

    public updateFroalaContent(newContent?: string) {
        if (newContent === this.currentContents) {
            return;
        }

        // Only update the internal contents for a change not caused by the user
        // We'll manage user initiated changes in this component
        this.currentContents = newContent;
        if (this.currentContents !== this.froalaContents && !this.maskInternalContentsUpdate) {
            this.froalaContents = this.currentContents;

            if (!this.froalaContentHasBeenInitialised) {
                this.froalaContentHasBeenInitialised = true;
            }

            // The froala event sometimes doesn't fire, so just do it explicitly.
            this.handleHtmlUpdate(this.currentContents);
        }
    }

    public setIsDisabled(isDisabled: boolean) {
        this.editorQueuedCaller.call((editor) => {
            if (isDisabled) {
                editor.edit.off();

                if (this.onlyShowToolbarWhenFocussed) {
                    editor.toolbar.hide();
                }
            } else {
                editor.edit.on();
            }
        });
    }

    public moveCursorToEnd() {
        this.editorQueuedCaller.call((editor) => {
            // this is from Froala support: https://stackoverflow.com/questions/34245599/set-the-caret-at-the-end-of-the-content-in-froala-2
            editor.selection.setAtEnd(editor.$el.get(0));
            editor.selection.restore();
        });
    }

    public generateFroalaOptions() {
        const self = this;

        const defaultToolbars = generateDefaultToolbars(this.showCodeView);
        const defaultButtons = generateDefaultButtons();
        const froalaOptions: Partial<FroalaOptions> = {
            attribution: false,
            key: AdaptClientConfiguration.FroalaKey,
            placeholderText: this.placeholderText,
            listAdvancedTypes: false,
            toolbarSticky: false,            // it would be great to enable this and get it to work - maybe our css overrides are killing it?
            videoResponsive: true,
            videoUpload: false,
            imageUploadURL: this.uploadUrl,
            imageDefaultWidth: 0, // this will remove the 300px default from Froala - Laura asked for it for Suzie
            heightMin: this.minHeight,
            toolbarButtons: defaultToolbars.standard,
            toolbarButtonsMD: defaultToolbars.standard,
            toolbarButtonsSM: defaultToolbars.compact,
            toolbarButtonsXS: defaultToolbars.compact,
            paragraphFormat: generateDefaultParagraphFormat(),
            fontFamilySelection: true,
            fontSizeSelection: true,
            paragraphFormatSelection: true,
            requestHeaders: {
                Accept: "application/json, text/plain, */*",
                Authorization: "Bearer " + this.token,          // used by froala's image upload plugin
            },
            dragInline: false,
            requestWithCORS: true,
            requestWithCredentials: true,
            imageInsertButtons: defaultButtons.imageInsertButtons,
            imageEditButtons: defaultButtons.imageEditButtons,
            linkEditButtons: defaultButtons.linkEditButtons,
            linkAlwaysBlank: true,
            linkNoOpener: true,
            linkNoReferrer: true,
            pasteAllowedStyleProps: ["background-color", "text-align", "vertical-align", "width"],
            pasteDeniedAttrs: ["class", "id"],
            events: {
                initialized() {
                    // froala provides the editor as this.
                    self.onInitialized(this);
                },
                blur() {
                    self.onBlur(this);
                },
                focus() {
                    self.onFocus(this);
                },
                "url.linked": function (link: any) {
                    if (link instanceof HTMLAnchorElement) {
                        // clean up repeated "noopener noreferrer" due to froala bug
                        link.rel = "noopener noreferrer";
                    }

                    return true;
                },
                "paste.afterCleanup": function (html) {
                    // Matches repeated "noopener noreferrer" to work around froala bug where it keeps adding more on
                    const referrerString = "noopener noreferrer";
                    const regex = new RegExp(`${referrerString}(?:\\s+${referrerString})+`, "g");
                    return html.replace(regex, referrerString);
                },
                contentChanged() {
                    self.handleHtmlUpdate(this.html.get(), true);
                },
                "commands.after": function (cmd1) {
                    if (cmd1 === "html") {
                        self.handleHtmlUpdate(this.html.get(), true, this.codeView.isActive());
                    }
                },
                "keydown": function (e) {
                    self.keyDown(e, this);
                },
            },
            iconsTemplate: "font_awesome_5",
            colorsHEXInput: false,
            customEditorActions: this.editorActions,
        };

        if (this.forceSize) {
            const toolbars = generateDefaultToolbars(this.showCodeView);
            const toolbar = toolbars[this.forceSize];

            if (toolbar) {
                froalaOptions.toolbarButtons = toolbar;
                froalaOptions.toolbarButtonsMD = toolbar;
                froalaOptions.toolbarButtonsSM = toolbar;
                froalaOptions.toolbarButtonsXS = toolbar;
            }
        }

        return froalaOptions;

        function generateDefaultToolbars(showCodeView: boolean) {
            // taken from https://www.froala.com/wysiwyg-editor/docs/options#toolbarButtons
            // from html-editor.component.ts

            const standardButtons = [
                "ai", "bold", "italic", "underline",
                "align", "formatOL", "formatUL", "outdent",
                "indent", "textColor", "backgroundColor",
                "insertTable", "insertDocLink", "insertImage", "insertVideo", "insertLumiaLink",
                // the 16 buttons above are visible by default - the ones below are not
                "fullscreen", "help", "horizontalLine", "clearFormatting",
            ];
            if (showCodeView) {
                standardButtons.push("html");
            }

            const compactButtons = [
                "ai", "bold", "italic", "underline", "formatOL", "formatUL", "insertTable", "insertDocLink", "insertImage", "insertVideo", "insertLumiaLink",
                // the 11 buttons above are visible by default - the ones below are not
                "paragraphFormat", "fontFamily", "fontSize", "paragraphStyle", "lineHeight", "quote",
                "align", "outdent", "indent",
                "textColor", "backgroundColor",
                "fullscreen", "help", "horizontalLine", "clearFormatting",
            ];
            if (showCodeView) {
                compactButtons.push("html");
            }

            return {
                standard: {
                    moreParagraph: {
                        buttons: ["paragraphFormat", "fontFamily", "fontSize", "paragraphStyle", "lineHeight", "quote"],
                        buttonsVisible: 1,
                    },
                    moreMisc: {
                        buttons: standardButtons,
                        buttonsVisible: 16,
                        align: "left",
                    },
                } as Partial<ToolbarButtons>,
                compact: {
                    moreMisc: {
                        buttons: compactButtons,
                        buttonsVisible: 11,
                        align: "left",
                    },
                } as Partial<ToolbarButtons>,
            };
        }

        function generateDefaultButtons() {
            return {
                imageInsertButtons: ["imageUpload"],
                imageEditButtons: ["imageReplace", "imageAlign", "imageRemove", "-", "imageDisplay", "imageStyle", "imageSize"],
                linkEditButtons: ["linkOpen", "editDocLink", "linkRemove"],
            };
        }

        function generateDefaultParagraphFormat() {
            return {
                H1: "Heading 1",
                H2: "Heading 2",
                H3: "Heading 3",
                H4: "Heading 4",
                N: "Paragraph",
                PRE: "Code",
            };
        }
    }

    @Autobind
    private onInitialized(editor: FroalaEditor) {
        this.editorQueuedCaller.setCallee(editor);

        // in the case where html-editor is only loaded after ng-if, contents will be blank
        // need to call this to set the contents (which is already defined in $onInit)
        editor.html.set(this.froalaContents ?? "");
        // Setting the content programmatically doesn't fire the froalaEditor.html.get event
        this.handleHtmlUpdate(this.froalaContents);

        if (this.onlyShowToolbarWhenFocussed) {
            editor.toolbar.hide();
        }

        if (this.useMentions) {
            const tribute = new Tribute({
                values: (_, cb) => new PeopleQueryUtilities(this.commonDataService)
                    .promiseToGetActivePeople()
                    .then((people) => people.map((person) => (
                        { key: person.fullName, value: person.personId, person })))
                    .then(cb),
                selectTemplate: (item: TributeItem<ITributeItemType>) => {
                    return `<span spellcheck="false" class="${MentionBoxClass}" data-mention-person-id="${item.original.value}">@${item.original.key}</span>`;
                },
                menuItemTemplate: (item) => MentionPersonComponent.generateHtmlForPerson(this.viewContainerRef, item.original.person),
            });
            tribute.attach(editor.el);
            editor.events.on("keydown", (e: JQuery.KeyDownEvent) => {
                if (e.which === FroalaEditor.KEYCODE.ENTER && tribute.isActive) {
                    // need this to prevent Froala from adding a <br> tag when the user presses enter with tribute active
                    // otherwise you won't be able to use enter to select person
                    return false;
                }
            }, true);

            const removeMentionOnChange = () => {
                const mentionBoxElement = this.getContainingMentionBoxElement(this.getSelectionParent(editor));
                if (mentionBoxElement) {
                    const container = mentionBoxElement.parentElement;
                    // delete the entire mention span - as this is not what's mentioned anymore
                    // - this includes deleting or adding anything in the mention box
                    mentionBoxElement.remove();
                    if (container && !container.textContent) {
                        // container empty after removing the element, remove the container too!
                        container.remove();
                    }
                } else {
                    const range = editor.selection.ranges(0) as Range;
                    const containerValue = range?.startContainer?.nodeValue as string;
                    if (containerValue?.startsWith("\u200B")) {
                        editor.selection.save();
                        // starts with zero width space - remove it or tribute won't trigger again!
                        range.startContainer.nodeValue = containerValue.slice(1);
                        setTimeout(() => editor.selection.restore()); // need to restore here or the cursor will be in the wrong place
                    }
                }
            };
            editor.events.on("keyup", (e: JQuery.KeyUpEvent) => {
                if ([FroalaEditor.KEYCODE.DELETE, FroalaEditor.KEYCODE.BACKSPACE].includes(e.which)) {
                    // only do this for delete and backspace to short-circuit the debounce - all others to leave them to contentChanged event
                    // - tried doing this on key down but that's before action, i.e. cursor not landing in the destination element yet
                    //   which will result in not deleting or deleting the wrong element
                    removeMentionOnChange();
                }
            });
            editor.events.on("contentChanged", () => {
                removeMentionOnChange(); // still need this for change without using keyboard
            });
            this.observeTributeContainer(() => {
                if (tribute.isActive) {
                    const selectionParent = this.getSelectionParent(editor);
                    if (this.getContainingMentionBoxElement(selectionParent)) {
                        // tribute active in a mention box - close it or we will have mention within mention!
                        // hideMenu not exposed through the type but it is there and required to close the menu
                        (tribute as any).hideMenu?.();
                    }
                }
            });
        }
    }

    private getSelectionParent(editor: FroalaEditor) {
        const range = editor.selection.ranges(0) as Range;
        return range.startContainer?.parentElement as HTMLElement;
    }

    private getContainingMentionBoxElement(element?: HTMLElement): HTMLElement | undefined {
        if (element?.classList?.contains(MentionBoxClass)) {
            return element;
        } else if (element?.parentElement) {
            return this.getContainingMentionBoxElement(element.parentElement);
        }

        return undefined;
    }

    private observeTributeContainer(callback: () => unknown) {
        // there is no callback when tribute pops - so manually make one here to prevent tribute dialog from popping
        // within a mention box
        const trigger = new Subject<void>();
        trigger.pipe(
            throttleTime(100),
            this.takeUntilDestroyed(),
        ).subscribe(() => callback());

        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node instanceof HTMLElement && node.classList.contains("tribute-container")) {
                        trigger.next();

                        // Now observe the style attribute of the newly added tribute container
                        const tributeObserver = new MutationObserver((tributeMutations) => {
                            tributeMutations.forEach((tributeMutation) => {
                                if (tributeMutation.type === "attributes" && tributeMutation.attributeName === "style") {
                                    trigger.next();
                                }
                            });
                        });

                        tributeObserver.observe(node, {
                            attributes: true, // Watch for attribute changes
                            attributeFilter: ["style"], // Only watch for changes to the 'style' attribute
                        });
                    }
                });
            });
        });

        observer.observe(document.body, {
            childList: true, // Watch for the addition of child nodes
            subtree: true, // Watch the entire subtree for changes
        });
    }

    @Autobind
    private keyDown(e: JQuery.KeyDownEvent, editor: FroalaEditor) {
        if (e.which === FroalaEditor.KEYCODE.SPACE) {
            const selection = editor.selection.get() as Selection;
            const node = selection.focusNode;

            if (node?.nodeType === Node.TEXT_NODE && node.textContent) {
                const text = node.textContent.trim();

                // Match "* " or "1."
                // but only when not already in a list
                if (text.match(/^(\*|-|1\.)$/) && node.parentElement?.tagName !== "LI") {
                    // stop propagating this keydown so we don't add an extra space to the first list item
                    e.preventDefault();

                    // replace the text node with the right list type
                    this.replaceNodeWithList(editor, node, text.startsWith("*") || text.startsWith("-") ? "UL" : "OL");
                }
            }
        }
    }

    @Autobind
    private onBlur(editor: FroalaEditor) {
        // save selection to be restored when we gain focus again
        editor.selection.save();
        this.hasFocus = false;
        this.maskInternalContentsUpdate = false;
        this.froalaContents = this.currentContents;

        if (this.onlyShowToolbarWhenFocussed) {
            editor.toolbar.hide();
        }
    }

    @Autobind
    private onFocus(editor: FroalaEditor) {
        this.hasFocus = true;
        editor.selection.restore();

        if (this.onlyShowToolbarWhenFocussed && !this.readonly) {
            editor.toolbar.show();
        }
    }

    @Autobind
    private handleHtmlUpdate(html?: string, fromFroalaEvent = false, isCodeViewActive = false) {
        const processedContent = this.sanitizeAndProcessHtml(html);
        const hasValidContent = this.validateHtml(processedContent) && !isCodeViewActive;

        // Update the model content everytime the user makes changes to handle the edge case
        // where a user makes changes that require processing (e.g. trailing new lines)
        // and clicks the save button while they have focus in the editor. This way when clicking
        // save they will save the latest content (but not necessarily see this in the editor)
        if (this.froalaContentHasBeenInitialised) {
            this.currentContents = processedContent;
            if (fromFroalaEvent) {
                // maskInternalContentsUpdate is used to block content being updated after you have manually updated the content
                // in the editor and still has focus.
                // It is set to false onBlur and should only be set to hasFocus this is a callback from froala editor event.
                // Note that froala editor event won't be raised if the content is changed from model but not through the editor.
                //
                // This will also limit the component @Output event to only emit if it is changed from the editor.
                // i.e. if the content is changed from component @Input, there is no output required.
                // (this will stop the [(contents)] from being toggled repeatedly if contents is changed quickly
                //  value1, value2, emit changed contents to value1 again which get passed into component, emit for value2..,
                //  emit for value1 again from the previous emit with value1, emit for value 2...)
                this.maskInternalContentsUpdate = this.hasFocus;

                if (html !== this.contents) {
                    this.contents = html;
                    this.contentsChange.emit(this.contents);
                }

                this.isValidChange.emit(hasValidContent);
                this.isInCodeView = isCodeViewActive;
                this.handleCodeViewToggle();
            }
        }

        this.contentScrollerUpdater$.next();
    }

    private handleCodeViewToggle() {
        if (this.contentScroller) {
            const scrollViewElement = jQuery(this.contentScroller.element()).get(0);
            if (scrollViewElement) {
                if (this.isInCodeView) {
                    // hide this element in code view as code view is a separate textarea
                    scrollViewElement.style.display = "none";
                    // parent need overflow auto for code view
                    scrollViewElement.parentElement!.style.overflow = "auto";
                } else {
                    // show the content scroller again, delete the style override for display
                    scrollViewElement.style.display = "";
                    // reset overflow override when switching back to editor view
                    scrollViewElement.parentElement!.style.overflow = "";
                }
            }
        }
    }

    private async updateContentScroller() {
        if (!this.contentScroller) {
            return;
        }

        await this.contentScroller.update(); // update the scroll view to ensure it reflects the new content size
        if (!this.isInCodeView) {
            // only need this when not in code view - code view appears to have already handled scrolling to cursor position in its textarea
            this.editorQueuedCaller.call((editor) => {
                // Check if cursor is at the end of the document
                const range = editor.selection.ranges(0) as Range;
                if (range?.endContainer instanceof Element) {
                    const cursorElement = range.endContainer as Element;

                    const parentWrapper = this.elementRef!.nativeElement.querySelector(".fr-wrapper") as Element;
                    if (parentWrapper && !ElementUtilities.isElementInView(cursorElement, parentWrapper)) {
                        // cursor is not in view -> scroll to it
                        this.contentScroller?.scrollToElement(cursorElement);
                    }
                }
            });
        }
    }

    private validateHtml(html?: string) {
        let hasValidContent = true;

        if (this.required) {
            hasValidContent = !!html;
        }

        if (this.elementRef!.nativeElement && this.required) {
            const children = Array.from(this.elementRef!.nativeElement.children);
            if (hasValidContent) {
                children.forEach((child: HTMLElement) => child.classList.remove("adaptEditor-required"));
            } else {
                children.forEach((child: HTMLElement) => child.classList.add("adaptEditor-required"));
            }
        }

        return hasValidContent;
    }

    private sanitizeAndProcessHtml(html?: string) {
        if (!html) {
            return "";
        }

        return StringUtilities.trimHtml(html);
    }

    private replaceNodeWithList(editor: FroalaEditor, node: Node, listType: ListType) {
        // remove the list text node (e.g. "1.", "-", "*")
        // then use the lists plugin to create the correct list
        node.parentNode?.removeChild(node);
        editor.lists.format(listType);
    }
}
